/**
 * src/router.tsx
 *
 * TanStack Router config — code-defined route tree (no file-based router
 * gen step needed). Auth is gated at the root: any route under "/" runs
 * an `onEnter`-style guard that bounces to /login when no API key is
 * stored.
 *
 * Route tree:
 *   /login        — set + persist x-api-key
 *   /             — Layout (sidebar + outlet)
 *     /           → Health (default)
 *     /alerts     → live alert feed
 *     /scrapers   → scraper grid + cron controls
 *     /healer     → auto-healer log + manual triggers
 *     /dbview     → read-only Mongo DB inspector
 *     /deals      → admin deal explorer (Tab 2 in plan)
 *     /anchors    → anchor freshness + modelKey lookup + missing anchors
 *     /review     → review-calibration tab (Tab 5 in plan)
 *     /users      → admin user inspection (list + drawer)
 */

import {
  createRootRoute,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import Layout from './components/Layout';
import Health from './pages/Health';
import Alerts from './pages/Alerts';
import ScrapersLayout, { pickDefaultScrapersTab } from './pages/scrapers/ScrapersLayout';
import ManageTab from './pages/scrapers/ManageTab';
import DiagnoseTab from './pages/scrapers/DiagnoseTab';
import RemediateTab from './pages/scrapers/RemediateTab';
import OnboardTab from './pages/scrapers/OnboardTab';
import Healer from './pages/Healer';
import DbView from './pages/DbView';
import Deals from './pages/Deals';
import Delivered from './pages/Delivered';
import Anchors from './pages/Anchors';
import Review from './pages/Review';
import Users from './pages/Users';
import Market from './pages/Market';
import Pulse from './pages/Pulse';
import Signals from './pages/Signals';
import AI from './pages/AI';
import Reader from './pages/Reader';
import Login from './pages/Login';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
});

const teleportAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/x-teleport-auth',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
  component: () => null,
});

const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: Layout,
});

const healthRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/',
  component: Health,
});

const alertsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/alerts',
  component: Alerts,
});

// Scrapers section: layout shell + 3 sub-tabs. Bare /scrapers redirects
// to the user's last-chosen tab (Manage on first visit) so muscle
// memory survives, while /scrapers/diagnose, /scrapers/onboard,
// /scrapers/manage all render the layout + their content.
const scrapersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/scrapers',
  component: ScrapersLayout,
});

const scrapersIndexRoute = createRoute({
  getParentRoute: () => scrapersRoute,
  path: '/',
  beforeLoad: () => { throw redirect({ to: pickDefaultScrapersTab() }); },
  component: () => null,
});

const scrapersDiagnoseRoute = createRoute({
  getParentRoute: () => scrapersRoute,
  path: '/diagnose',
  component: DiagnoseTab,
});

const scrapersRemediateRoute = createRoute({
  getParentRoute: () => scrapersRoute,
  path: '/remediate',
  component: RemediateTab,
});

const scrapersOnboardRoute = createRoute({
  getParentRoute: () => scrapersRoute,
  path: '/onboard',
  component: OnboardTab,
});

const scrapersManageRoute = createRoute({
  getParentRoute: () => scrapersRoute,
  path: '/manage',
  component: ManageTab,
});

const healerRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/healer',
  component: Healer,
});

const dbViewRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/dbview',
  component: DbView,
});

const dealsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/deals',
  component: Deals,
});

const deliveredRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/delivered',
  component: Delivered,
});

const aiRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/ai',
  component: AI,
});

const readerRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/reader',
  component: Reader,
});

const anchorsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/anchors',
  component: Anchors,
});

const reviewRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/review',
  component: Review,
});

const usersRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/users',
  component: Users,
});

const marketRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/market',
  component: Market,
});

const signalsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/signals',
  component: Signals,
});

const pulseRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/pulse',
  component: Pulse,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  teleportAuthRoute,
  protectedLayoutRoute.addChildren([
    healthRoute,
    alertsRoute,
    scrapersRoute.addChildren([
      scrapersIndexRoute,
      scrapersDiagnoseRoute,
      scrapersRemediateRoute,
      scrapersOnboardRoute,
      scrapersManageRoute,
    ]),
    healerRoute,
    dbViewRoute,
    dealsRoute,
    deliveredRoute,
    anchorsRoute,
    aiRoute,
    readerRoute,
    reviewRoute,
    usersRoute,
    marketRoute,
    signalsRoute,
    pulseRoute,
  ]),
]);
